/**
 * Invalidate Confirm Dialog Component
 *
 * Confirmation dialog for invalidating a single cache namespace.
 */

import { Trash2, Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { CacheNamespace } from '@/types/cache';
import { CACHE_NAMESPACE_LABELS } from '@/types/cache';
import { useState } from 'react';

interface InvalidateConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  namespace: CacheNamespace | null;
  keyCount: number;
  onConfirm: (id?: string) => void;
  isInvalidating: boolean;
}

/**
 * Get ID label based on namespace
 */
function getIdLabelForNamespace(namespace: CacheNamespace | null): string {
  switch (namespace) {
    case 'menu':
      return 'Restaurant ID (optional)';
    case 'modifier-display':
      return 'Restaurant ID (optional)';
    case 'modifier-global':
      return 'Chain Modifier ID (optional)';
    case 'vehicle-reference':
      return 'Not applicable';
    default:
      return 'ID (optional)';
  }
}

/**
 * Check if namespace supports ID filtering
 */
function supportsIdFilter(namespace: CacheNamespace | null): boolean {
  return namespace !== 'vehicle-reference' && namespace !== null;
}

/**
 * Invalidate Confirm Dialog
 * Confirmation with optional ID filter for targeted invalidation
 */
export function InvalidateConfirmDialog({
  open,
  onOpenChange,
  namespace,
  keyCount,
  onConfirm,
  isInvalidating,
}: InvalidateConfirmDialogProps) {
  const [specificId, setSpecificId] = useState('');

  const handleConfirm = () => {
    onConfirm(specificId.trim() || undefined);
    setSpecificId('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setSpecificId('');
    }
    onOpenChange(newOpen);
  };

  if (!namespace) return null;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Invalidate {CACHE_NAMESPACE_LABELS[namespace]}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {specificId.trim()
              ? `This will invalidate cache entries for ID "${specificId}" in the ${CACHE_NAMESPACE_LABELS[namespace]} namespace.`
              : `This will invalidate all ${keyCount.toLocaleString()} keys in the ${CACHE_NAMESPACE_LABELS[namespace]} namespace.`}
            <br />
            <strong>This action cannot be undone.</strong>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {supportsIdFilter(namespace) && (
          <div className="py-4">
            <Label htmlFor="specific-id" className="text-sm font-medium">
              {getIdLabelForNamespace(namespace)}
            </Label>
            <Input
              id="specific-id"
              placeholder="Leave empty to invalidate all"
              value={specificId}
              onChange={e => setSpecificId(e.target.value)}
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Optionally specify an ID to only invalidate related cache entries.
            </p>
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isInvalidating}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isInvalidating}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isInvalidating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Invalidating...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Invalidate
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
